// apps/widget/src/lib/postmessage.ts
//
// The widget's side of the bridge. Origin equality is checked on every inbound
// message, and every outbound message is addressed to a specific origin — never
// '*', which on a page with another embedded third party hands them the payload.

export type OutboundType =
  | 'gs:ready'
  | 'gs:resize'
  | 'gs:layout'
  | 'gs:guard'
  | 'gs:close'
  | 'gs:scan:started'
  | 'gs:scan:complete'
  | 'gs:lead:captured'
  | 'gs:cta:clicked';

let hostOrigin: string | null = null;
let allowedOrigins: string[] = [];

/**
 * The host tells us its origin at init; we accept it only if the tenant's
 * allowlist contains it. Trusting the claim alone would let any page embed the
 * widget and receive its events.
 */
export function configureBridge(origins: string[]): void {
  allowedOrigins = origins.map((o) => o.toLowerCase());
}

export function isEmbedded(): boolean {
  return window.parent !== window;
}

export function send(type: OutboundType, payload: Record<string, unknown> = {}): void {
  if (!isEmbedded() || !hostOrigin) return;
  window.parent.postMessage({ type, ...payload }, hostOrigin);
}

export interface BridgeHandlers {
  onOpen?: () => void;
  onClose?: () => void;
  onSetLocale?: (locale: string) => void;
  /** Someone tried to close mid-scan; confirm before letting it happen. */
  onCloseRequested?: () => void;
}

export function listen(handlers: BridgeHandlers): () => void {
  const onMessage = (e: MessageEvent): void => {
    const data = e.data as { type?: string; locale?: string; hostOrigin?: string };
    if (!data?.type?.startsWith?.('gs:')) return;

    if (data.type === 'gs:init') {
      const claimed = (data.hostOrigin ?? e.origin).toLowerCase();
      // The claim is only accepted if it matches the actual message origin AND
      // the tenant's allowlist.
      if (claimed !== e.origin.toLowerCase()) return;
      if (allowedOrigins.length > 0 && !allowedOrigins.includes(claimed)) return;

      hostOrigin = e.origin;
      send('gs:ready');
      return;
    }

    if (!hostOrigin || e.origin !== hostOrigin) return;

    switch (data.type) {
      case 'gs:open':
        handlers.onOpen?.();
        break;
      case 'gs:close':
        handlers.onClose?.();
        break;
      case 'gs:setLocale':
        if (data.locale) handlers.onSetLocale?.(data.locale);
        break;
      case 'gs:close-requested':
        handlers.onCloseRequested?.();
        break;
    }
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/**
 * Height changes reported to the host, for inline embeds. Throttled through
 * rAF — a ResizeObserver firing on every layout pass would flood the host page
 * with messages during the report's staged reveal.
 */
export function reportHeight(element: HTMLElement): () => void {
  let frame = 0;
  let lastHeight = 0;

  const observer = new ResizeObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const height = Math.ceil(element.getBoundingClientRect().height);
      if (height !== lastHeight && height > 0) {
        lastHeight = height;
        send('gs:resize', { height });
      }
    });
  });

  observer.observe(element);
  return () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
  };
}
